import { z } from 'zod';

import { PROPERTY_MEDIA_MAX_FILES } from '@/lib/constants/propertyMedia';
import { SUBMARKET_OPTIONS } from '@/lib/constants/propertyOptions';
import type {
  DateRangeFilterValue,
  FloorLevelCode,
  FloorPlanCanvasConfig,
  FloorPlanMode,
  PaginationInput,
  PropertyFormValues,
  PropertyListFilters,
  RangeFilterValue,
  RentBasisType,
  RentFrequencyType,
  UnitAvailabilityStatus,
  UnitFormValues,
  UnitListFilters,
  UnitPossessionTimeline,
} from '@/lib/types/property';
import type { Json } from '@/lib/supabase/types.gen';

const POSTAL_CODE_PATTERN = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

const PROVINCE_VALUES = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
] as const;

const STREET_TYPE_VALUES = [
  'Ave',
  'Blvd',
  'Cir',
  'Cres',
  'Dr',
  'Gate',
  'Lane',
  'Pkwy',
  'Rd',
  'St',
  'Terr',
  'Way',
] as const;

const STREET_DIRECTION_VALUES = ['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW'] as const;

const ASSET_TYPE_VALUES = ['industrial', 'flex', 'retail', 'office'] as const;
const FLOOR_PLAN_MODE_VALUES = [
  'hotspot',
  'blank_canvas',
] as const satisfies readonly FloorPlanMode[];
const FLOOR_LEVEL_VALUES = [
  'B3',
  'B2',
  'B1',
  'G',
  'M',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
  '24',
  '25',
  '26',
  '27',
  '28',
  '29',
  '30',
] as const satisfies readonly FloorLevelCode[];
const RENT_BASIS_VALUES = [
  'net',
  'gross',
  'triple_net',
] as const satisfies readonly RentBasisType[];
const RENT_FREQUENCY_VALUES = [
  'monthly',
  'quarterly',
  'annually',
  'weekly',
  'daily',
] as const satisfies readonly RentFrequencyType[];
const UNIT_AVAILABILITY_VALUES = [
  'available',
  'unavailable',
  'leased',
] as const satisfies readonly UnitAvailabilityStatus[];
const UNIT_POSSESSION_TIMELINE_VALUES = [
  'immediate',
  'negotiable',
] as const satisfies readonly UnitPossessionTimeline[];
const SUBMARKET_VALUES = SUBMARKET_OPTIONS;

const uuidSchema = z
  .string({ invalid_type_error: 'Must be a valid UUID' })
  .uuid('Must be a valid UUID');

const optionalUuid = uuidSchema.optional();

const optionalNullableUuid = z
  .union([uuidSchema, z.null()])
  .optional()
  .transform((value) => (value === undefined ? undefined : value));

const isoDateSchema = z
  .string({ required_error: 'Date is required' })
  .trim()
  .refine((value) => !Number.isNaN(Date.parse(value)), {
    message: 'Must be a valid date',
  });

const optionalIsoDate = isoDateSchema.optional().nullable();

function coerceNumber(value: unknown): unknown {
  if (value === '' || value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : value;
  }
  return value;
}

function positiveNumber(label: string) {
  return z.preprocess(
    coerceNumber,
    z
      .number({ invalid_type_error: `${label} must be a number` })
      .gt(0, `${label} must be greater than zero`),
  );
}

function optionalPositiveNumber(label: string) {
  return z.preprocess(
    coerceNumber,
    z
      .number({ invalid_type_error: `${label} must be a number` })
      .gt(0, `${label} must be greater than zero`)
      .nullable()
      .optional(),
  );
}

function optionalNonNegativeNumber(label: string) {
  return z.preprocess(
    coerceNumber,
    z
      .number({ invalid_type_error: `${label} must be a number` })
      .gte(0, `${label} must be zero or greater`)
      .nullable()
      .optional(),
  );
}

function hasAtMostTwoDecimals(value: number): boolean {
  const scaled = value * 100;
  return Number.isFinite(scaled) && Math.abs(scaled - Math.round(scaled)) < 1e-9;
}

function currencyPositiveNumber(label: string) {
  return z.preprocess(
    coerceNumber,
    z
      .number({ invalid_type_error: `${label} must be a number` })
      .gt(0, `${label} must be greater than zero`)
      .refine(hasAtMostTwoDecimals, `${label} must have at most two decimal places`),
  );
}

function optionalCurrencyPositiveNumber(label: string) {
  return z.preprocess(
    coerceNumber,
    z
      .number({ invalid_type_error: `${label} must be a number` })
      .gt(0, `${label} must be greater than zero`)
      .refine(hasAtMostTwoDecimals, `${label} must have at most two decimal places`)
      .nullable()
      .optional(),
  );
}

const submarketSchema = z
  .string({ required_error: 'Submarket is required', invalid_type_error: 'Submarket is required' })
  .trim()
  .min(1, 'Submarket is required')
  .refine((value) => SUBMARKET_VALUES.includes(value as (typeof SUBMARKET_VALUES)[number]), {
    message: 'Submarket must match the supported list.',
  });

function optionalTrimmedString(label: string, max = 512) {
  return z.preprocess(
    (value) => {
      if (value === undefined || value === null) {
        return undefined;
      }
      if (typeof value !== 'string') {
        return value;
      }
      const trimmed = value.trim();
      return trimmed.length === 0 ? undefined : trimmed;
    },
    z
      .string({ invalid_type_error: `${label} must be a string` })
      .max(max, `${label} is too long`)
      .optional(),
  );
}

const nonEmptyString = (label: string, max = 200) =>
  z
    .string({
      required_error: `${label} is required`,
      invalid_type_error: `${label} must be a string`,
    })
    .trim()
    .min(1, `${label} is required`)
    .max(max, `${label} must be at most ${max.toString()} characters`);

const emailSchema = z
  .string({ required_error: 'Email is required', invalid_type_error: 'Email must be a string' })
  .trim()
  .email('Must be a valid email');

const urlSchema = z
  .string({ invalid_type_error: 'Must be a valid URL' })
  .trim()
  .url('Must be a valid URL');

const optionalUrl = urlSchema
  .optional()
  .nullable()
  .transform((value) => value ?? null);

const canvasValueSchema = z.union([z.string(), z.number(), z.boolean()]);

const floorPlanCanvasConfigSchema: z.ZodType<FloorPlanCanvasConfig> = z
  .object({
    gridSize: optionalPositiveNumber('Grid size').transform((value) => value ?? undefined),
    sfPerGrid: optionalPositiveNumber('Square footage per grid').transform(
      (value) => value ?? undefined,
    ),
    minDivisibleSqFt: optionalPositiveNumber('Minimum divisible square footage').transform(
      (value) => value ?? undefined,
    ),
    maxDrawSqFt: optionalPositiveNumber('Maximum draw square footage').transform(
      (value) => value ?? undefined,
    ),
    canvasWidth: optionalPositiveNumber('Canvas width').transform((value) => value ?? undefined),
    canvasHeight: optionalPositiveNumber('Canvas height').transform((value) => value ?? undefined),
    instructions: optionalTrimmedString('Instructions', 2000),
    snapToGrid: z.boolean().optional(),
  })
  .catchall(canvasValueSchema.optional())
  .default({}) as unknown as z.ZodType<FloorPlanCanvasConfig>;

const propertyAddressSchema = z.object({
  streetNumber: nonEmptyString('Street number', 16),
  streetName: nonEmptyString('Street name', 120),
  streetType: z.enum(STREET_TYPE_VALUES),
  streetDirection: z.enum(STREET_DIRECTION_VALUES).nullable().optional(),
  suiteNumber: optionalTrimmedString('Suite number', 16).nullable().optional(),
  city: nonEmptyString('City', 120),
  province: z.enum(PROVINCE_VALUES),
  postalCode: z
    .string({ required_error: 'Postal code is required' })
    .trim()
    .toUpperCase()
    .regex(POSTAL_CODE_PATTERN, 'Enter a valid Canadian postal code'),
});

export const propertyFormSchema: z.ZodType<PropertyFormValues> = z
  .object({
    id: optionalUuid,
    propertyCode: z
      .string({ required_error: 'Property code is required' })
      .trim()
      .min(3, 'Property code must be at least 3 characters long')
      .max(64, 'Property code must be at most 64 characters')
      .regex(
        /^[A-Za-z0-9_-]+$/,
        'Property code may only include letters, numbers, hyphens, and underscores',
      ),
    submarket: submarketSchema,
    assetType: z.enum(ASSET_TYPE_VALUES),
    totalRentableSf: positiveNumber('Total rentable square footage'),
    marketingSummary: nonEmptyString('Marketing summary', 2000).min(
      20,
      'Marketing summary must include at least 20 characters',
    ),
    heroMediaUrls: z
      .array(urlSchema)
      .max(
        PROPERTY_MEDIA_MAX_FILES,
        `Up to ${PROPERTY_MEDIA_MAX_FILES.toString()} hero images are allowed`,
      )
      .default([]),
    floorPlanMode: z.enum(FLOOR_PLAN_MODE_VALUES),
    floorPlanImageUrl: optionalUrl,
    floorPlanConfig: floorPlanCanvasConfigSchema,
    address: propertyAddressSchema,
    createdAt: optionalIsoDate,
    updatedAt: optionalIsoDate,
    createdBy: optionalNullableUuid,
    updatedBy: optionalNullableUuid,
  })
  .superRefine((data, ctx) => {
    if (data.heroMediaUrls.length > 0 && !data.heroMediaUrls[0]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Hero media URLs must be valid http(s) links',
        path: ['heroMediaUrls'],
      });
    }
  }) as unknown as z.ZodType<PropertyFormValues>;

const jsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonSchema),
    z.record(jsonSchema),
  ]),
);

export const unitFormSchema: z.ZodType<UnitFormValues> = z
  .object({
    id: optionalUuid,
    propertyId: uuidSchema,
    unitCode: nonEmptyString('Unit code', 32),
    unitLabel: nonEmptyString('Unit label', 64),
    floorLevel: z.enum(FLOOR_LEVEL_VALUES).nullable(),
    areaSf: positiveNumber('Area (SF)'),
    minDivisibleSf: optionalNonNegativeNumber('Minimum divisible SF'),
    availabilityStart: optionalIsoDate,
    availabilityStatus: z.enum(UNIT_AVAILABILITY_VALUES),
    possessionTimeline: z.enum(UNIT_POSSESSION_TIMELINE_VALUES).default('immediate'),
    rentBasis: z.enum(RENT_BASIS_VALUES),
    netRentPerSf: currencyPositiveNumber('Net rent per SF'),
    additionalRentPerSf: optionalCurrencyPositiveNumber('Additional rent per SF'),
    rentFrequency: z.enum(RENT_FREQUENCY_VALUES),
    incentives: optionalTrimmedString('Incentives', 512).nullable().optional(),
    potentiallyDivisible: z.boolean(),
    contactEmail: emailSchema,
    heroImageUrl: optionalUrl,
    galleryImageUrls: z.array(urlSchema).optional().default([]),
    planGeometry: jsonSchema.optional(),
    displayOnPublicWebsite: z.boolean(),
    published: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.minDivisibleSf != null && data.minDivisibleSf > data.areaSf) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Minimum divisible SF cannot exceed the total area',
        path: ['minDivisibleSf'],
      });
    }
  }) as unknown as z.ZodType<UnitFormValues>;

function createRangeSchema(label: string): z.ZodType<RangeFilterValue> {
  return z
    .object({
      min: optionalNonNegativeNumber(`${label} minimum`),
      max: optionalNonNegativeNumber(`${label} maximum`),
    })
    .partial()
    .superRefine((range, ctx) => {
      if (range.min != null && range.max != null && range.min > range.max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} minimum cannot exceed maximum`,
          path: ['max'],
        });
      }
    }) as z.ZodType<RangeFilterValue>;
}

function createDateRangeSchema(label: string): z.ZodType<DateRangeFilterValue> {
  return z
    .object({
      start: optionalIsoDate,
      end: optionalIsoDate,
    })
    .partial()
    .superRefine((range, ctx) => {
      if (
        typeof range.start === 'string' &&
        range.start.length > 0 &&
        typeof range.end === 'string' &&
        range.end.length > 0 &&
        Date.parse(range.start) > Date.parse(range.end)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} start must be on or before the end date`,
          path: ['end'],
        });
      }
    }) as z.ZodType<DateRangeFilterValue>;
}

export const propertyFilterSchema: z.ZodType<PropertyListFilters> = z
  .object({
    submarkets: z.array(submarketSchema).min(1).optional(),
    assetTypes: z.array(z.enum(ASSET_TYPE_VALUES)).min(1).optional(),
    rentableSquareFeet: createRangeSchema('Rentable square footage').optional(),
    rentPerSquareFoot: createRangeSchema('Rent per square foot').optional(),
    availability: createDateRangeSchema('Availability').optional(),
    readiness: z.array(z.enum(UNIT_AVAILABILITY_VALUES)).min(1).optional(),
    incentivesOnly: z.boolean().optional(),
    potentiallyDivisible: z.boolean().nullable().optional(),
    hasVacancy: z.boolean().optional(),
  })
  .partial();

export const unitFilterSchema: z.ZodType<UnitListFilters> = z
  .object({
    propertyId: uuidSchema.optional(),
    floorLevels: z.array(z.enum(FLOOR_LEVEL_VALUES)).optional(),
    readiness: z.array(z.enum(UNIT_AVAILABILITY_VALUES)).optional(),
    displayOnPublicWebsite: z.boolean().optional(),
    published: z.boolean().optional(),
  })
  .partial();

export const paginationSchema: z.ZodType<PaginationInput> = z.object({
  page: z
    .number({ required_error: 'Page is required' })
    .int('Page must be an integer')
    .min(1, 'Page must be at least 1'),
  pageSize: z
    .number({ required_error: 'Page size is required' })
    .int('Page size must be an integer')
    .min(1, 'Page size must be at least 1')
    .max(200, 'Page size must be at most 200'),
});

type _EnsurePropertyValuesMatch =
  z.infer<typeof propertyFormSchema> extends PropertyFormValues
    ? PropertyFormValues extends z.infer<typeof propertyFormSchema>
      ? true
      : never
    : never;

type _EnsureUnitValuesMatch =
  z.infer<typeof unitFormSchema> extends UnitFormValues
    ? UnitFormValues extends z.infer<typeof unitFormSchema>
      ? true
      : never
    : never;

type _EnsurePropertyFilterMatches =
  z.infer<typeof propertyFilterSchema> extends PropertyListFilters
    ? PropertyListFilters extends z.infer<typeof propertyFilterSchema>
      ? true
      : never
    : never;

type _EnsureUnitFilterMatches =
  z.infer<typeof unitFilterSchema> extends UnitListFilters
    ? UnitListFilters extends z.infer<typeof unitFilterSchema>
      ? true
      : never
    : never;

type _EnsurePaginationMatches =
  z.infer<typeof paginationSchema> extends PaginationInput
    ? PaginationInput extends z.infer<typeof paginationSchema>
      ? true
      : never
    : never;
